import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { AppConfig } from '@plato/app-meta/app-config-type';
import { SrCustomerStatus } from '@plato/data/starrocks/types';
import { OrderIcon } from '@plato/ui-legacy/assets/icons/order-icon';
import { QuoteIcon } from '@plato/ui-legacy/assets/icons/quote-icon';
import { InfoTag } from '@plato/ui-legacy/components/ui/info-tag';
import type { Namespace, TFunction } from 'i18next';
import {
    ClockIcon,
    ExpandIcon,
    RepeatIcon,
    SparklesIcon,
    TrendingDownIcon,
    TrendingUpIcon,
    TriangleAlertIcon,
} from 'lucide-react';
import { forwardRef, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerListItem } from '../../../../server/queries/customer-query-many-with-filters.ts';

export type CustomerInfoTagsProps = {
    customer: CustomerListItem;
    maxTags: number;
};

const infoTagsMap = {
    'revenue-delta': (customer, { t, appConfig }) => {
        const useYtdTimeframe = appConfig.defaultTimeframe === 'YTD';
        const deltaDifferencePercent = useYtdTimeframe
            ? customer.revenueYtdDeltaPercentage
            : customer.deltaDifferencePercentTtm;

        if (!deltaDifferencePercent || Math.abs(deltaDifferencePercent) <= 0.001) {
            return;
        }

        const isPositive = deltaDifferencePercent > 0;
        const useOrderSumMetric = appConfig.defaultMetric === 'Order_Sum';

        const deltaFormat = {
            revenueDelta: deltaDifferencePercent,
            formatParams: {
                revenueDelta: {
                    style: 'percent',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                    signDisplay: 'always',
                },
            },
        } as const;

        return (
            <InfoTag icon={isPositive ? TrendingUpIcon : TrendingDownIcon} size="xs">
                {useOrderSumMetric
                    ? t('analysis_section__snippets__order-sum-delta', { ns: 'dashboard', ...deltaFormat })
                    : t('analysis_section__snippets__revenue-delta', { ns: 'dashboard', ...deltaFormat })}
            </InfoTag>
        );
    },
    'days-since-first-order': (customer, { t }) => {
        if (!customer.daysSinceFirstOrder) {
            return;
        }

        return (
            <InfoTag icon={ClockIcon} size="xs">
                {t('analysis_section__snippets__days-since-first-order', {
                    ns: 'dashboard',
                    days: customer.daysSinceFirstOrder,
                })}
            </InfoTag>
        );
    },
    'days-since-last-order': (customer, { t }) => {
        if (!customer.daysSinceLastOrder) {
            return;
        }

        return (
            <InfoTag icon={ClockIcon} size="xs">
                {t('analysis_section__snippets__days-since-last-order', {
                    ns: 'dashboard',
                    days: customer.daysSinceLastOrder,
                })}
            </InfoTag>
        );
    },
    'open-quotes': (customer, { t }) => {
        if (!customer.openQuotesCount || customer.openQuotesCount <= 0) {
            return;
        }

        return (
            <InfoTag icon={QuoteIcon} size="xs">
                {t('analysis_section__snippets__open_quotes', {
                    ns: 'dashboard',
                    count: customer.openQuotesCount,
                })}
            </InfoTag>
        );
    },
    'cross-sell': (customer, { t, appConfig }) => {
        if (!customer.crossSellsCount) {
            return;
        }

        // Follows the same flag as the card title and the list filter, so a row never
        // says "Cross-Sell" next to a filter that says "Never bought".
        return (
            <InfoTag icon={RepeatIcon} size="xs">
                {appConfig.enableNeverBoughtCrossSellCopy
                    ? t('analysis_section__snippets__cross_sell_never_bought', {
                          ns: 'dashboard',
                          count: customer.crossSellsCount,
                      })
                    : t('analysis_section__snippets__cross_sell', {
                          ns: 'dashboard',
                          count: customer.crossSellsCount,
                      })}
            </InfoTag>
        );
    },
    'share-of-wallet': (customer, { t }) => {
        if (!customer.shareOfWalletCount) {
            return;
        }

        return (
            <InfoTag icon={ExpandIcon} size="xs">
                {t('analysis_section__snippets__share_of_wallet', {
                    ns: 'dashboard',
                    count: customer.shareOfWalletCount,
                })}
            </InfoTag>
        );
    },
    'first-purchase': (customer, { t }) => {
        if (!customer.firstPurchaseCount) {
            return;
        }

        return (
            <InfoTag icon={OrderIcon} size="xs">
                {t('analysis_section__snippets__first_purchase', {
                    ns: 'dashboard',
                    count: customer.firstPurchaseCount,
                })}
            </InfoTag>
        );
    },
    churn: (customer, { t }) => {
        if (!customer.churnWarningsCount) {
            return;
        }

        return (
            <InfoTag icon={TriangleAlertIcon} size="xs">
                {t('analysis_section__snippets__churn_warning', {
                    ns: 'dashboard',
                    count: customer.churnWarningsCount,
                })}
            </InfoTag>
        );
    },
    'sales-insights': (customer, { t }) => {
        if (!customer.insightsCountUnused || customer.insightsCountUnused <= 0) {
            return;
        }

        return (
            <InfoTag icon={SparklesIcon} size="xs">
                {t('snippet_sales_insights', {
                    ns: 'dashboard',
                    count: customer.insightsCountUnused,
                })}
            </InfoTag>
        );
    },
} satisfies Record<
    string,
    (
        customer: CustomerListItem,
        opts: {
            t: TFunction<Namespace>;
            appConfig: AppConfig;
        },
    ) => ReactElement | undefined
>;

type InfoTagKey = keyof typeof infoTagsMap;

const infoTagsOrder = {
    [SrCustomerStatus.NEW]: [
        'days-since-first-order',
        'open-quotes',
        'cross-sell',
        'share-of-wallet',
        'first-purchase',
        'sales-insights',
    ],
    [SrCustomerStatus.ACTIVE]: [
        'revenue-delta',
        'cross-sell',
        'share-of-wallet',
        'first-purchase',
        'open-quotes',
        'days-since-last-order',
        'sales-insights',
    ],
    [SrCustomerStatus.AT_RISK]: [
        'revenue-delta',
        'days-since-last-order',
        'churn',
        'open-quotes',
        'sales-insights',
    ],
    [SrCustomerStatus.INACTIVE]: [
        'revenue-delta',
        'days-since-last-order',
        'churn',
        'open-quotes',
        'sales-insights',
    ],
    [SrCustomerStatus.DORMANT]: [
        'revenue-delta',
        'days-since-last-order',
        'churn',
        'open-quotes',
        'sales-insights',
    ],
    [SrCustomerStatus.BLOCKED]: [
        'revenue-delta',
        'days-since-last-order',
        'churn',
        'open-quotes',
        'sales-insights',
    ],
} satisfies Record<SrCustomerStatus, InfoTagKey[]>;

export const CustomerInfoTags = forwardRef<HTMLDivElement, CustomerInfoTagsProps>(
    ({ customer, maxTags }, ref) => {
        const { t } = useTranslation('dashboard');
        const appConfig = useAppConfig();

        // Generate final list of snippet tags based on priority order
        const snippetTags = useMemo(() => {
            const order = infoTagsOrder[customer.platoStatus];

            return order
                .map((key) => infoTagsMap[key](customer, { t, appConfig }))
                .filter(Boolean)
                .slice(0, maxTags);
        }, [customer, maxTags, t, appConfig]);

        if (snippetTags.length === 0) {
            return null;
        }

        return (
            <div ref={ref} className="flex flex-wrap gap-y-1 gap-x-2 items-center">
                {snippetTags}
            </div>
        );
    },
);

CustomerInfoTags.displayName = 'CustomerInfoTags';
